import { createClientOnlyFn } from '@tanstack/react-start'
import {
  ANALYTICS_SCHEMA_VERSION,
  type AnalyticsRequestContext,
  type AnalyticsAttributionTouch,
  type AnalyticsClientContext,
} from '@shared/analytics-contract'
import {
  getGoogleRecommendedEventName,
  type GoogleRecommendedEventName,
} from '@shared/analytics-vendor-manifest'

const DEFAULT_AMPLITUDE_API_KEY = 'c3a2bf744e040acf0b27c6064e22da6a'
const DEFAULT_GA_MEASUREMENT_ID = 'G-53CDHMCB9C'
const VENDOR_CONTEXT_TIMEOUT_MS = 250
const ANALYTICS_VISITOR_STORAGE_KEY = 'buildingregs.analytics.visitor.v1'
const ANALYTICS_SESSION_STORAGE_KEY = 'buildingregs.analytics.session.v1'
const ANALYTICS_FIRST_TOUCH_STORAGE_KEY =
  'buildingregs.analytics.first-touch.v1'
const ANALYTICS_LAST_TOUCH_STORAGE_KEY =
  'buildingregs.analytics.last-non-direct-touch.v1'

type AmplitudeOptions = {
  serverZone: 'EU'
  analytics: {
    autocapture: true
  }
  sessionReplay: {
    sampleRate: 1
  }
}

type AmplitudeModule = {
  Identify: new () => {
    set: (property: string, value: string | number | boolean) => unknown
  }
  flush: () => unknown
  getSessionId: () => number | undefined
  initAll: (apiKey: string, options: AmplitudeOptions) => Promise<void>
  identify: (identify: unknown) => unknown
  setDeviceId: (deviceId: string) => void
  setOptOut: (optOut: boolean) => void
  setUserId: (userId: string | undefined) => void
  track: (
    eventName: string,
    properties: Record<string, string | number | boolean | null>,
  ) => unknown
}
type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined
>

type AnalyticsWindow = Window & {
  __buildingRegsAnalyticsEnabled?: boolean
  __buildingRegsAmplitudeInitialized?: boolean
  clarity?: (...args: Array<any>) => void
  gtag?: (...args: Array<any>) => void
}

let amplitudeModulePromise: Promise<AmplitudeModule> | null = null
let amplitudeInitPromise: Promise<boolean> | null = null
let engagementStartedAt = Date.now()
let analyticsIdentityContext: {
  userId: string | null
  planId: string | null
  organizationId: string | null
  persona: string | null
  organizationRole: string | null
  organizationSizeBand: string | null
} = {
  userId: null,
  planId: null,
  organizationId: null,
  persona: null,
  organizationRole: null,
  organizationSizeBand: null,
}

const CLARITY_CONTEXT_TAG_NAMES = [
  'plan_id',
  'authentication_state',
  'persona',
  'organization_role',
  'organization_size_band',
  'entry_point',
  'first_touch_source',
] as const

export type ClientAnalyticsEventMap = {
  chat_entry_submitted: {
    entry_point: 'home'
    source: 'composer'
  }
  page_view: {
    page_path: string
    page_location: string
    page_title: string
  }
  persona_prompt_answered: {
    persona: string
    prompt_version: number
    source: 'post_first_verified_answer' | 'settings_profile'
  }
  persona_prompt_dismissed: {
    prompt_version: number
    source: 'post_first_verified_answer'
  }
}

export const enableAmplitudeAnalytics = createClientOnlyFn(() => {
  if (!isBrowser()) return Promise.resolve(false)

  const analyticsWindow = window as AnalyticsWindow
  analyticsWindow.__buildingRegsAnalyticsEnabled = true

  return initializeAmplitude()
})

function initializeAmplitude() {
  const analyticsWindow = window as AnalyticsWindow

  if (analyticsWindow.__buildingRegsAmplitudeInitialized) {
    return loadAmplitude().then((amplitude) => {
      syncAmplitudeOptOut(amplitude)
      return true
    })
  }

  if (!amplitudeInitPromise) {
    amplitudeInitPromise = loadAmplitude()
      .then(async (amplitude) => {
        await amplitude.initAll(getAmplitudeApiKey(), getAmplitudeOptions())
        amplitude.setDeviceId(getAnalyticsVisitorId())
        analyticsWindow.__buildingRegsAmplitudeInitialized = true
        syncAmplitudeOptOut(amplitude)
        return true
      })
      .catch((error) => {
        amplitudeInitPromise = null
        if (import.meta.env.DEV) {
          console.warn('Amplitude failed to initialize.', error)
        }
        return false
      })
  }

  return amplitudeInitPromise
}

export const disableAmplitudeAnalytics = createClientOnlyFn(() => {
  if (!isBrowser()) return

  const analyticsWindow = window as AnalyticsWindow
  analyticsWindow.__buildingRegsAnalyticsEnabled = false

  if (!amplitudeModulePromise) return

  void amplitudeModulePromise.then((amplitude) => {
    syncAmplitudeOptOut(amplitude)
    amplitude.flush()
  })
})

export const trackAnalyticsEvent = createClientOnlyFn(
  (eventName: string, properties: AnalyticsProperties = {}) => {
    if (!isBrowser()) return

    const analyticsWindow = window as AnalyticsWindow
    if (!analyticsWindow.__buildingRegsAnalyticsEnabled) return

    const normalizedEventName = normalizeAnalyticsEventName(eventName)
    const cleanedProperties = cleanAnalyticsProperties({
      ...getAutomaticAnalyticsProperties(),
      ...properties,
      event_id: createAnalyticsId(),
      analytics_schema_version: ANALYTICS_SCHEMA_VERSION,
    })
    trackGoogleAnalyticsEvent(
      analyticsWindow,
      normalizedEventName,
      cleanedProperties,
    )
    trackMicrosoftClarityEvent(
      analyticsWindow,
      normalizedEventName,
      cleanedProperties,
    )

    void enableAmplitudeAnalytics().then((enabled) => {
      if (!enabled) return
      void loadAmplitude().then((amplitude) => {
        amplitude.track(normalizedEventName, cleanedProperties)
      })
    })
  },
)

export function trackClientEvent<
  TEventName extends keyof ClientAnalyticsEventMap,
>(eventName: TEventName, properties: ClientAnalyticsEventMap[TEventName]) {
  return trackAnalyticsEvent(eventName, properties)
}

export const setAnalyticsIdentity = createClientOnlyFn(
  async (
    userId: string | null,
    context: {
      planId: string | null
      organizationId: string | null
      persona?: string | null
      organizationRole?: string | null
      organizationSizeBand?: string | null
    } = { planId: null, organizationId: null, persona: null },
  ) => {
    if (!isBrowser()) return

    analyticsIdentityContext = {
      userId,
      planId: context.planId,
      organizationId: context.organizationId,
      persona: context.persona ?? null,
      organizationRole: context.organizationRole ?? null,
      organizationSizeBand: context.organizationSizeBand ?? null,
    }

    const analyticsWindow = window as AnalyticsWindow
    const clientContext = getAnalyticsClientContext()
    analyticsWindow.gtag?.('config', getGoogleAnalyticsMeasurementId(), {
      send_page_view: false,
      user_id: userId ?? undefined,
    })
    const userProperties = {
      authentication_state: userId ? 'signed_in' : 'anonymous',
      first_touch_source: clientContext.firstTouch.source,
      ...(context.planId ? { plan_id: context.planId } : {}),
      ...(context.persona ? { persona: context.persona } : {}),
      ...(context.organizationRole
        ? { organization_role: context.organizationRole }
        : {}),
      ...(context.organizationSizeBand
        ? { organization_size_band: context.organizationSizeBand }
        : {}),
    }
    analyticsWindow.gtag?.('set', 'user_properties', userProperties)
    identifyMicrosoftClaritySession(
      analyticsWindow,
      userId ?? clientContext.visitorId,
      clientContext.sessionId,
    )
    syncMicrosoftClarityContextTags(analyticsWindow, {
      plan_id: context.planId,
      authentication_state: userId ? 'signed_in' : 'anonymous',
      persona: context.persona ?? null,
      first_touch_source: clientContext.firstTouch.source,
      organization_role: context.organizationRole ?? null,
      organization_size_band: context.organizationSizeBand ?? null,
    })

    const enabled = await enableAmplitudeAnalytics()
    if (!enabled) return
    const amplitude = await loadAmplitude()
    amplitude.setUserId(userId ?? undefined)
    const identify = new amplitude.Identify()
    for (const [property, value] of Object.entries(userProperties)) {
      identify.set(property, value)
    }
    amplitude.identify(identify)
  },
)

function loadAmplitude() {
  amplitudeModulePromise =
    amplitudeModulePromise ??
    import('@amplitude/unified').then(
      (amplitude) => amplitude as AmplitudeModule,
    )
  return amplitudeModulePromise
}

function syncAmplitudeOptOut(amplitude: AmplitudeModule) {
  const analyticsWindow = window as AnalyticsWindow
  amplitude.setOptOut(!analyticsWindow.__buildingRegsAnalyticsEnabled)
}

function getAutomaticAnalyticsProperties() {
  const context = getAnalyticsClientContext()
  return {
    analytics_visitor_id: context.visitorId,
    analytics_session_id: context.sessionId,
    first_touch_source: context.firstTouch.source,
    first_touch_medium: context.firstTouch.medium,
    first_touch_campaign: context.firstTouch.campaign,
    last_non_direct_source: context.lastNonDirectTouch?.source ?? null,
    last_non_direct_medium: context.lastNonDirectTouch?.medium ?? null,
    last_non_direct_campaign: context.lastNonDirectTouch?.campaign ?? null,
    authentication_state: analyticsIdentityContext.userId
      ? 'signed_in'
      : 'anonymous',
    plan_id: analyticsIdentityContext.planId,
    organization_id: analyticsIdentityContext.organizationId,
    persona: analyticsIdentityContext.persona,
    organization_role: analyticsIdentityContext.organizationRole,
    organization_size_band: analyticsIdentityContext.organizationSizeBand,
  }
}

export function getAnalyticsClientContext(): AnalyticsClientContext {
  const currentTouch = buildCurrentAttributionTouch()
  const storedFirstTouch = readStoredAttribution(
    ANALYTICS_FIRST_TOUCH_STORAGE_KEY,
  )
  const shouldReplaceDirectFirstTouch =
    storedFirstTouch !== null &&
    !isNonDirectTouch(storedFirstTouch) &&
    isNonDirectTouch(currentTouch)
  const firstTouch =
    !storedFirstTouch || shouldReplaceDirectFirstTouch
      ? currentTouch
      : storedFirstTouch
  if (!storedFirstTouch || shouldReplaceDirectFirstTouch) {
    writeStoredAttribution(ANALYTICS_FIRST_TOUCH_STORAGE_KEY, firstTouch)
  }

  const storedLastNonDirect = readStoredAttribution(
    ANALYTICS_LAST_TOUCH_STORAGE_KEY,
  )
  const currentTouchIsNonDirect = isNonDirectTouch(currentTouch)
  const shouldUpdateLastNonDirect =
    currentTouchIsNonDirect &&
    (!storedLastNonDirect ||
      getAttributionTouchKey(currentTouch) !==
        getAttributionTouchKey(storedLastNonDirect))
  const lastNonDirectTouch = shouldUpdateLastNonDirect
    ? currentTouch
    : storedLastNonDirect
  if (shouldUpdateLastNonDirect) {
    writeStoredAttribution(ANALYTICS_LAST_TOUCH_STORAGE_KEY, currentTouch)
  }

  return {
    visitorId: getAnalyticsVisitorId(),
    sessionId: getAnalyticsSessionId(),
    firstTouch,
    lastNonDirectTouch,
  }
}

export async function getAnalyticsRequestContext(): Promise<AnalyticsRequestContext> {
  const clientContext = getAnalyticsClientContext()
  const [googleClientId, googleSessionId, amplitudeSessionId] =
    await Promise.all([
      getGoogleAnalyticsValue('client_id', (value) =>
        typeof value === 'string' && value.trim() ? value.trim() : null,
      ),
      getGoogleAnalyticsValue('session_id', (value) =>
        typeof value === 'string' && /^\d+$/.test(value) ? value : null,
      ),
      getAmplitudeSessionId(),
    ])
  const now = Date.now()
  const googleEngagementTimeMsec = Math.max(1, now - engagementStartedAt)
  engagementStartedAt = now

  return {
    ...clientContext,
    googleClientId,
    googleSessionId,
    googleEngagementTimeMsec,
    amplitudeSessionId,
  }
}

function getGoogleAnalyticsValue<T>(
  field: 'client_id' | 'session_id',
  normalize: (value: unknown) => T,
) {
  const analyticsWindow = window as AnalyticsWindow
  if (!analyticsWindow.gtag) return Promise.resolve(normalize(null))

  return resolveWithinTimeout<T>((resolve) => {
    analyticsWindow.gtag?.(
      'get',
      getGoogleAnalyticsMeasurementId(),
      field,
      (value: unknown) => resolve(normalize(value)),
    )
  }, normalize(null))
}

async function getAmplitudeSessionId() {
  const enabled = await resolvePromiseWithinTimeout(
    initializeAmplitude(),
    false,
  )
  if (!enabled) return null

  const amplitude = await loadAmplitude()
  const sessionId = amplitude.getSessionId()
  return typeof sessionId === 'number' && Number.isSafeInteger(sessionId)
    ? sessionId
    : null
}

function resolveWithinTimeout<T>(
  start: (resolve: (value: T) => void) => void,
  fallback: T,
) {
  return new Promise<T>((resolve) => {
    let settled = false
    const finish = (value: T) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeout)
      resolve(value)
    }
    const timeout = window.setTimeout(
      () => finish(fallback),
      VENDOR_CONTEXT_TIMEOUT_MS,
    )

    try {
      start(finish)
    } catch (_) {
      finish(fallback)
    }
  })
}

function resolvePromiseWithinTimeout<T>(promise: Promise<T>, fallback: T) {
  return resolveWithinTimeout<T>((resolve) => {
    void promise.then(resolve, () => resolve(fallback))
  }, fallback)
}

function getAttributionTouchKey(touch: AnalyticsAttributionTouch) {
  const { captured_at: _capturedAt, ...attributionIdentity } = touch
  return JSON.stringify(attributionIdentity)
}

function getAnalyticsVisitorId() {
  return getOrCreateStoredAnalyticsId(
    window.localStorage,
    ANALYTICS_VISITOR_STORAGE_KEY,
  )
}

function getAnalyticsSessionId() {
  return getOrCreateStoredAnalyticsId(
    window.sessionStorage,
    ANALYTICS_SESSION_STORAGE_KEY,
  )
}

function getOrCreateStoredAnalyticsId(storage: Storage, key: string) {
  try {
    const existing = storage.getItem(key)
    if (existing) return existing
    const created = createAnalyticsId()
    storage.setItem(key, created)
    return created
  } catch (_) {
    return createAnalyticsId()
  }
}

function createAnalyticsId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

function buildCurrentAttributionTouch(): AnalyticsAttributionTouch {
  const search = new URLSearchParams(window.location.search)
  const referrerHost = getReferrerHost()
  const campaignSource = search.get('utm_source')
  const campaignMedium = search.get('utm_medium')

  return {
    source: campaignSource ?? referrerHost ?? 'direct',
    medium: campaignMedium ?? (referrerHost ? 'referral' : 'none'),
    campaign: search.get('utm_campaign'),
    term: search.get('utm_term'),
    content: search.get('utm_content'),
    landing_path: window.location.pathname,
    referrer_host: referrerHost,
    has_gclid: search.has('gclid'),
    has_msclkid: search.has('msclkid'),
    has_fbclid: search.has('fbclid'),
    captured_at: new Date().toISOString(),
  }
}

function getReferrerHost() {
  if (!document.referrer) return null
  try {
    const referrer = new URL(document.referrer)
    return referrer.hostname === window.location.hostname
      ? null
      : referrer.hostname
  } catch (_) {
    return null
  }
}

function isNonDirectTouch(touch: AnalyticsAttributionTouch) {
  return touch.source !== 'direct' || touch.medium !== 'none'
}

function readStoredAttribution(key: string) {
  try {
    const stored = window.localStorage.getItem(key)
    if (!stored) return null
    return JSON.parse(stored) as AnalyticsAttributionTouch
  } catch (_) {
    return null
  }
}

function writeStoredAttribution(key: string, touch: AnalyticsAttributionTouch) {
  try {
    window.localStorage.setItem(key, JSON.stringify(touch))
  } catch (_) {
    // Attribution remains available for this page when storage is blocked.
  }
}

function cleanAnalyticsProperties(
  properties: AnalyticsProperties,
): Record<string, string | number | boolean | null> {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined),
  ) as Record<string, string | number | boolean | null>
}

function trackGoogleAnalyticsEvent(
  analyticsWindow: AnalyticsWindow,
  eventName: string,
  properties: Record<string, string | number | boolean | null>,
) {
  if (!analyticsWindow.gtag) return false

  analyticsWindow.gtag('event', eventName, properties)
  const recommendedEventName = getGoogleRecommendedEventName(eventName)
  if (recommendedEventName) {
    analyticsWindow.gtag(
      'event',
      recommendedEventName,
      buildGoogleRecommendedClientProperties(properties, recommendedEventName),
    )
  }
  return true
}

function buildGoogleRecommendedClientProperties(
  properties: Record<string, string | number | boolean | null>,
  eventName: GoogleRecommendedEventName,
) {
  if (eventName === 'sign_up' || eventName === 'login') {
    const methodProperty =
      eventName === 'login' ? 'auth_method' : 'signup_method'
    return {
      method:
        typeof properties[methodProperty] === 'string'
          ? properties[methodProperty]
          : 'unknown',
    }
  }

  const planId =
    typeof properties.plan_id === 'string'
      ? properties.plan_id
      : typeof properties.current_plan_id === 'string'
        ? properties.current_plan_id
        : 'unknown'
  const amountMinor = properties.amount_minor
  const amount =
    typeof amountMinor === 'number' && Number.isFinite(amountMinor)
      ? amountMinor / 100
      : null
  const currency =
    typeof properties.currency === 'string'
      ? properties.currency.toUpperCase()
      : null
  const item = {
    item_id: planId,
    item_name: planId,
    ...(amount === null ? {} : { price: amount }),
    quantity: 1,
  }
  const commerceProperties = {
    ...(amount === null ? {} : { value: amount }),
    ...(currency ? { currency } : {}),
    items: [item],
  }

  if (
    eventName === 'view_item_list' ||
    eventName === 'select_item' ||
    eventName === 'begin_checkout' ||
    eventName === 'add_payment_info'
  ) {
    return commerceProperties
  }

  return {
    transaction_id:
      typeof properties.payment_intent_id === 'string'
        ? properties.payment_intent_id
        : String(properties.event_id),
    ...commerceProperties,
  }
}

function trackMicrosoftClarityEvent(
  analyticsWindow: AnalyticsWindow,
  eventName: string,
  properties: Record<string, string | number | boolean | null>,
) {
  if (!analyticsWindow.clarity) return
  syncMicrosoftClarityContextTags(analyticsWindow, properties)
  analyticsWindow.clarity('event', eventName)
}

function identifyMicrosoftClaritySession(
  analyticsWindow: AnalyticsWindow,
  customId: string,
  sessionId: string,
) {
  analyticsWindow.clarity?.('identify', customId, sessionId)
}

function syncMicrosoftClarityContextTags(
  analyticsWindow: AnalyticsWindow,
  properties: Record<string, string | number | boolean | null>,
) {
  const clarity = analyticsWindow.clarity
  if (!clarity) return

  for (const tagName of CLARITY_CONTEXT_TAG_NAMES) {
    const value = properties[tagName]
    if (typeof value !== 'string') continue
    const normalizedValue = value.trim().slice(0, 255)
    if (!normalizedValue) continue
    clarity('set', tagName, normalizedValue)
  }

  if ('usage_remaining' in properties) {
    const remaining = properties.usage_remaining
    const usageState =
      typeof remaining === 'number'
        ? remaining <= 0
          ? 'exhausted'
          : 'available'
        : 'unknown'
    clarity('set', 'usage_state', usageState)
  }
}

function normalizeAnalyticsEventName(eventName: string) {
  const normalized = eventName
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

  const eventWithLeadingLetter = /^[a-z]/.test(normalized)
    ? normalized
    : `event_${normalized || 'interaction'}`

  return eventWithLeadingLetter.slice(0, 40).replace(/_+$/g, '')
}

function getAmplitudeApiKey() {
  return (
    import.meta.env.VITE_AMPLITUDE_API_KEY ||
    process.env.VITE_AMPLITUDE_API_KEY ||
    DEFAULT_AMPLITUDE_API_KEY
  )
}

function getGoogleAnalyticsMeasurementId() {
  return (
    import.meta.env.VITE_GA_MEASUREMENT_ID ||
    process.env.VITE_GA_MEASUREMENT_ID ||
    DEFAULT_GA_MEASUREMENT_ID
  )
}

function getAmplitudeOptions(): AmplitudeOptions {
  return {
    serverZone: 'EU',
    analytics: {
      autocapture: true,
    },
    sessionReplay: {
      sampleRate: 1,
    },
  }
}

function isBrowser() {
  return typeof window !== 'undefined'
}
